/**
 * Suplidos de un borrador de factura — `OD-0052` (ACCEPTED).
 *
 * Un suplido es dinero que el usuario pagó EN NOMBRE Y POR CUENTA de su
 * cliente: una tasa, un certificado, un burofax. No lleva IVA ni retención y no
 * va a la Agencia Tributaria. Por eso vive aquí, fuera de las líneas: metido
 * como una línea más, le cargaría el IVA que no le toca.
 *
 * ```text
 * D-01  cada uno lleva su casilla, y nace SIN marcar: lo afirma el usuario,
 *       Faktelio no puede saberlo
 * D-05  hasta 20; el importe se escribe como texto y lo valida la API
 * ```
 *
 * **No suma nada**: el total con suplidos llega calculado de la API, como el
 * resto (`URS §129`).
 */
import type { SuplidoEditable } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo, Casilla } from "./primitivas";

/** `OD-0052 D-05`. La API lo vuelve a comprobar; aquí sólo se esconde el botón. */
export const MAX_SUPLIDOS = 20;

export const SUPLIDO_VACIO: SuplidoEditable = {
  description: "",
  amount: "",
  paidTo: "",
  reference: "",
  onBehalfConfirmed: false,
};

export function EditorSuplidos({
  prefijo,
  suplidos,
  onCambio,
  errores,
}: {
  prefijo: string;
  suplidos: readonly SuplidoEditable[];
  onCambio: (suplidos: readonly SuplidoEditable[]) => void;
  /** Los códigos de error de la API tal cual, con su clave `disbursements.<i>.<campo>`. */
  errores: Readonly<Record<string, string>>;
}) {
  const texto = (code: string | undefined): string | null =>
    code === undefined ? null : (t.suplidos.errores[code] ?? t.presupuesto.errores[code] ?? t.comun.errorGenerico);
  const errorDe = (i: number, campo: string): string | null => texto(errores[`disbursements.${i}.${campo}`]);
  const cambiar = (i: number, parche: Partial<SuplidoEditable>) => onCambio(suplidos.map((s, j) => (j === i ? { ...s, ...parche } : s)));
  const general = texto(errores.disbursements);

  return (
    <>
      <h3>{t.suplidos.titulo}</h3>
      <p className="ayuda-neutra">{t.suplidos.ayuda}</p>
      {/* Plegada: quien ya sabe lo que es no tiene por qué leerla cada vez. Pero
          está aquí, junto a la casilla, porque lo que la casilla afirma lo
          afirma el usuario (D-01). */}
      <details className="desglose guia-suplidos">
        <summary>{t.suplidos.guia.titulo}</summary>
        {t.suplidos.guia.secciones.map((s) => (
          <section key={s.titulo}>
            <h4>{s.titulo}</h4>
            {s.parrafos.map((p) => (
              <p key={p}>{p}</p>
            ))}
            {s.puntos.length > 0 ? (
              <ul>
                {s.puntos.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </details>
      {general !== null ? <Aviso tono="error">{general}</Aviso> : null}
      {suplidos.map((s, i) => (
        <fieldset key={i} className="linea-documento">
          <legend>{t.suplidos.leyenda(i + 1)}</legend>
          <div className="fila-campos">
            <Campo
              id={`${prefijo}-suplido-concepto-${i}`}
              etiqueta={t.suplidos.concepto}
              valor={s.description}
              onChange={(v) => cambiar(i, { description: v })}
              autoComplete="off"
              error={errorDe(i, "description")}
            />
            <Campo
              id={`${prefijo}-suplido-importe-${i}`}
              etiqueta={t.suplidos.importe}
              valor={s.amount}
              onChange={(v) => cambiar(i, { amount: v })}
              autoComplete="off"
              error={errorDe(i, "amount")}
            />
          </div>
          <div className="fila-campos">
            <Campo
              id={`${prefijo}-suplido-pagado-${i}`}
              etiqueta={t.suplidos.pagadoA}
              valor={s.paidTo}
              onChange={(v) => cambiar(i, { paidTo: v })}
              autoComplete="off"
              requerido={false}
              error={errorDe(i, "paidTo")}
            />
            <Campo
              id={`${prefijo}-suplido-referencia-${i}`}
              etiqueta={t.suplidos.referencia}
              valor={s.reference}
              onChange={(v) => cambiar(i, { reference: v })}
              ayuda={t.suplidos.ayudaReferencia}
              autoComplete="off"
              requerido={false}
              error={errorDe(i, "reference")}
            />
          </div>
          <Casilla
            id={`${prefijo}-suplido-confirmado-${i}`}
            etiqueta={t.suplidos.confirmacion}
            marcada={s.onBehalfConfirmed}
            onChange={(v) => cambiar(i, { onBehalfConfirmed: v })}
            error={errorDe(i, "onBehalfConfirmed")}
          />
          <Boton variante="secundario" onClick={() => onCambio(suplidos.filter((_, j) => j !== i))}>
            {t.suplidos.quitar}
          </Boton>
        </fieldset>
      ))}
      {suplidos.length < MAX_SUPLIDOS ? (
        <Boton variante="secundario" onClick={() => onCambio([...suplidos, SUPLIDO_VACIO])}>
          {t.suplidos.anadir}
        </Boton>
      ) : null}
    </>
  );
}
